import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { GemIcon } from '@sorare/core/src/atoms/icons/GemIcon';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { isTypeFn } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { thinMaterial } from '@sorare/core/src/style/utils';

import { Attribute } from '#src/components/buyActions/PaymentBox/CardOverview/index.tsx';
import { VoucherPaymentDialog } from '#src/components/commonCard/BuyPacks/GemsPack/VoucherPaymentDialog/index.tsx';

import ellipse from '../assets/ellipse.svg';
import type { BundledPurchasableItem_bundledPurchasableItem } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 2 })<{ background: string }>`
  position: relative;
  border-radius: var(--double-unit);
  padding: var(--intermediate-unit);
  justify-content: space-between;
  text-align: center;
  ${thinMaterial}
  border: 1px solid var(--c-nd-150);

  &.useInGameCurrencyPack {
    background: no-repeat top / contain url(${props => props.background});
    background-size: contain;
    background-position: top;
    aspect-ratio: 296 / 368;
  }
`;

const Container = styled(Vertical).attrs({ center: true, gap: 2 })`
  flex-grow: 1;
  justify-content: flex-start;
`;

const backgroundCss = css`
  --square-width: var(--double-and-a-half-unit);
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.7) 100%),
    repeating-linear-gradient(
      to left,
      var(--c-nd-150) 0px,
      var(--c-nd-150) 1px,
      transparent 1px,
      transparent var(--square-width)
    ),
    repeating-linear-gradient(
      to bottom,
      var(--c-nd-150) 0px,
      var(--c-nd-150) 1px,
      transparent 1px,
      transparent var(--square-width)
    ),
    linear-gradient(180deg, #284b30 0%, rgba(0, 0, 0, 0.7) 100%);
`;

const Leftcolumn = styled(Horizontal).attrs({ gap: 2, center: true })`
  padding: var(--double-unit);
  ${backgroundCss}
  img {
    max-width: 120px;
  }

  @media ${laptopAndAbove} {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    --square-width: calc(var(--unit) * 5);
    img {
      max-width: none;
    }
  }
`;

const AttributeWrapper = styled(Horizontal).attrs({ gap: 0.5 })`
  border-radius: var(--double-unit);
  background-color: rgba(var(--c-rgb-white), 0.1);
`;

const Value = styled(AttributeWrapper)`
  padding: var(--half-unit) var(--unit);
`;

const PictureWrapper = styled(Vertical).attrs({ center: true })`
  position: relative;
  isolation: isolate;
`;

const Ellipse = styled(Img).attrs({ src: ellipse })`
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
`;

const Picture = styled(Img)`
  width: calc(10 * var(--unit));
  height: calc(10 * var(--unit));
  object-fit: cover;
`;

const PackPicture = styled(Img)`
  width: calc(30 * var(--unit));
  height: calc(15 * var(--unit));
  object-fit: contain;
`;

type Props = {
  bundledPurchasableItem: BundledPurchasableItem_bundledPurchasableItem;
  onPackPurchased: (offerId: string) => void;
  voucherId: string;
};

type BundleDisplayItem =
  | { key: string; kind: 'pack'; value: string }
  | { key: string; kind: 'gems'; value: number };

export const BundledPurchasableItem = ({
  bundledPurchasableItem,
  onPackPurchased,
  voucherId,
}: Props) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const [dialogOpened, setDialogOpened] = useState(false);

  const {
    id: purchasableItemId,
    pictureUrl,
    price: bundledPrice,
    purchasableItems,
    slug,
  } = bundledPurchasableItem;

  const packConfigItems = purchasableItems.filter(
    isTypeFn('PackConfigPurchasableItem')
  );
  const inGameCurrencyItems = purchasableItems.filter(
    isTypeFn('InGameCurrencyPurchasableItem')
  );
  const displayItems: BundleDisplayItem[] = [
    ...packConfigItems.map(item => ({
      key: item.slug,
      kind: 'pack' as const,
      value: item.cardPack.title,
    })),
    ...inGameCurrencyItems.map(item => ({
      key: item.slug,
      kind: 'gems' as const,
      value: item.baseAmount,
    })),
  ];
  const firstPackConfigItem = packConfigItems[0];
  const firstInGameCurrencyItem = inGameCurrencyItems[0];
  const fallbackDisplayItem: BundleDisplayItem = {
    key: slug,
    kind: 'pack',
    value: slug,
  };
  const allDisplayItems =
    displayItems.length > 0 ? displayItems : [fallbackDisplayItem];
  const paymentBoxPictureUrl =
    pictureUrl ||
    firstPackConfigItem?.pictureUrl ||
    firstPackConfigItem?.cardPack.pictureUrl ||
    firstInGameCurrencyItem?.pictureUrl ||
    '';

  const price = toMonetaryAmount(bundledPrice);
  const { main } = useAmountWithConversion({ monetaryAmount: price });

  if (price.isZero) return null;

  return (
    <>
      <Root background={paymentBoxPictureUrl}>
        <Container>
          <PictureWrapper>
            <Ellipse />
            <Picture
              src={paymentBoxPictureUrl}
              alt=""
              width="320"
              height="290"
            />
          </PictureWrapper>

          <Vertical gap={0.5} center>
            {allDisplayItems.map(item =>
              item.kind === 'gems' ? (
                <Horizontal key={item.key}>
                  <BodyS>{item.value}</BodyS>
                  <GemIcon size={2} />
                </Horizontal>
              ) : (
                <BodyS key={item.key}>{item.value}</BodyS>
              )
            )}
          </Vertical>
        </Container>

        <Button
          size="small"
          onClick={() => {
            setDialogOpened(true);
          }}
        >
          {main}
        </Button>
      </Root>
      <VoucherPaymentDialog
        open={dialogOpened}
        onClose={() => setDialogOpened(false)}
        purchasableItemId={purchasableItemId}
        voucherId={voucherId}
        price={price}
        onPackPurchased={onPackPurchased}
        tokenPreview={
          <Leftcolumn>
            <PackPicture src={paymentBoxPictureUrl} alt="" />
            <Vertical gap={0.5} center>
              {allDisplayItems.map(item => (
                <Attribute
                  key={item.key}
                  label={
                    item.kind === 'gems' ? (
                      <FormattedMessage
                        id="BundledPurchasableItem.previewBaseAmount"
                        defaultMessage="Bundle of Gems"
                      />
                    ) : (
                      <FormattedMessage
                        id="BundledPurchasableItem.previewName"
                        defaultMessage="Pack"
                      />
                    )
                  }
                  value={
                    item.kind === 'gems' ? (
                      <Value>
                        +{item.value} <GemIcon />
                      </Value>
                    ) : (
                      <Value>{item.value}</Value>
                    )
                  }
                />
              ))}
            </Vertical>
          </Leftcolumn>
        }
      />
    </>
  );
};

BundledPurchasableItem.fragments = {
  bundledPurchasableItem: gql`
    fragment BundledPurchasableItem_bundledPurchasableItem on BundledPurchasableItem {
      slug
      id
      pictureUrl
      price {
        ...useMonetaryAmount_monetaryAmount
      }
      purchasableItems {
        slug
        ... on PackConfigPurchasableItem {
          slug
          pictureUrl
          cardPack {
            slug
            title
            pictureUrl
          }
        }
        ... on InGameCurrencyPurchasableItem {
          slug
          pictureUrl
          baseAmount
        }
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<BundledPurchasableItem_bundledPurchasableItem>,
};
